/**
 * The library, as templates a new plan can be copied from — „Nowy program"'s and „Nowy plan"'s
 * first step.
 *
 * Moved here from `features/assign-plan/model/assignPrograms.ts` on 23 September 2026. That module
 * was the assign dialog's client-first program picker; the client entry now starts in THIS dialog,
 * where a template is copied into the builder rather than handed over as it stands, and the picker
 * came with it. Its two filters and its search are unchanged.
 *
 * ── Why the feature projects it rather than reusing the library screen's projection ──────────
 * `pages/plan-library/model/planLibraryView.ts` powers a screen with a scope switch, four facets, a
 * sort and a preview panel. A feature may not import a page, and copying that projection would
 * bring a dozen fields this list has no use for. What the READ is — the query options — is shared,
 * in `entities/plan-draft`, and so is the cover rule (`planCoverPair`).
 *
 * ── Only a row with a usable id, only a mode this build can name ─────────────────────────────
 * A row answering `0`, a negative id or none at all is a guaranteed 404 the moment its full read is
 * asked for. A scheduling mode this build cannot name has no canvas in the builder the copy would
 * open in. Filtering here rather than refusing on selection is the honest shape — an option that can
 * only be refused is not an option.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. `t` is a parameter.
 */

import {
  planCategoryKey,
  planCoverPair,
  planLengthLabel,
  schedulingModeFromApi,
  type PlanCategoryKey,
} from '~/entities/plan-draft';
import type { TrainWithMeModulesPlansDtosResponsesPlanTemplateListItem as PlanTemplateListItem } from '~/shared/api/generated/model';
import { foldPolish } from '~/shared/lib/foldPolish';
import type { Translate } from '~/shared/lib/localeTranslate';

/** One template, as a row in the picker and as the preview beside it. */
export interface TemplateChoice {
  readonly templateId: number;
  readonly name: string;
  /** „3 tygodnie · 3 dni" — the length and the rhythm, one line under the name. */
  readonly meta: string;
  /** „Atleta Hybryda · 3 tygodnie · Kalendarz" — what the foot says beside „Dalej" once it is chosen. */
  readonly summary: string;
  /** Kalendarz / Własnym tempem — the fact that decides what the copy's details step can ask. */
  readonly systemLabel: string;
  readonly selfPaced: boolean;
  readonly category: PlanCategoryKey | null;
  readonly thumbUrl: string;
  readonly heroUrl: string;
  /** The preview's chips, in the order it draws them: the system, the length, the rhythm, the clients. */
  readonly facts: readonly string[];
}

/** Which scheduling system the list is narrowed to. */
export type TemplateFacet = 'all' | 'date' | 'selfPaced';

/** The facet's options, in the order the segmented control draws them. */
export const TEMPLATE_FACETS: readonly TemplateFacet[] = ['all', 'date', 'selfPaced'];

/** The trainer's library, as templates to copy. */
export function templateChoices(
  rows: readonly PlanTemplateListItem[] | null | undefined,
  t: Translate,
): TemplateChoice[] {
  const choices: TemplateChoice[] = [];

  for (const row of rows ?? []) {
    if (typeof row.id !== 'number' || row.id <= 0) continue;
    // The builder's own reading of the mode, so a row is offered exactly when its copy can open —
    // a blank reads as the calendar there, and `readPlanDocument` refuses on the same `null`.
    const mode = schedulingModeFromApi(row.schedulingMode);
    if (mode === null) continue;

    const selfPaced = mode === 'selfpaced';
    const name = row.name?.trim() ?? '';
    const category = planCategoryKey(row.category);
    const cycles = row.totalCycleCount ?? 0;
    const days = row.daysPerWeek ?? 0;
    const clients = row.assignedClientCount ?? 0;

    const systemLabel = t(
      selfPaced ? 'web:planCreate.system.selfPaced.title' : 'web:planCreate.system.dateBased.title',
    );
    const length = planLengthLabel(mode, cycles, t);

    choices.push({
      templateId: row.id,
      name,
      meta: t('web:planCreate.pick.meta', {
        length,
        rhythm: t(selfPaced ? 'web:planCreate.pick.sessions' : 'web:planCreate.pick.days', {
          count: days,
        }),
      }),
      summary: t('web:planCreate.pick.summary', { name, length, system: systemLabel }),
      systemLabel,
      selfPaced,
      category,
      ...planCoverPair(row.coverImageUrl, category, name),
      facts: [
        systemLabel,
        length,
        t(selfPaced ? 'web:planCreate.pick.perCycle' : 'web:planCreate.pick.perWeek', {
          count: days,
        }),
        clients === 0
          ? t('web:planCreate.pick.noClients')
          : t('web:planCreate.pick.clients', { count: clients }),
      ],
    });
  }

  return choices;
}

/**
 * What the facet and the search leave, and the chosen template they would have hidden.
 *
 * `matches` are the rows the facet and the search term leave, and nothing else: the count and the
 * no-match state read them alone. The search is diacritic-insensitive — the same fold the client
 * picker and the library use, so „sila" finds „Siła".
 *
 * `pinned` is the chosen template when the filters leave it out, `null` otherwise. The list is one
 * radio group with the scratch row, so filtering the chosen row away left a group with no checked
 * radio while the preview and „Dalej" still meant that template: Tab landed on the scratch row, and
 * an arrow key there silently replaced a choice the trainer could no longer see. It is kept apart
 * rather than mixed into the matches because it is not one — counting it said „1 szablon" under a
 * search nothing answered, and the no-match sentence never appeared.
 *
 * `pinnedId` is the anchor the flow holds — the template chosen when the filters last changed, not
 * the live choice: choosing a match must not unmount the pinned row above it and pull the list up
 * under the pointer.
 */
export interface TemplateMatches {
  readonly matches: TemplateChoice[];
  readonly pinned: TemplateChoice | null;
}

export function matchingTemplates(
  choices: readonly TemplateChoice[],
  query: string,
  facet: TemplateFacet,
  pinnedId: number | null = null,
): TemplateMatches {
  const needle = foldPolish(query.trim());
  const matches = choices.filter(
    (choice) =>
      (facet === 'all' || choice.selfPaced === (facet === 'selfPaced')) &&
      (needle === '' || foldPolish(choice.name).includes(needle)),
  );
  const pinned =
    pinnedId === null || matches.some((choice) => choice.templateId === pinnedId)
      ? null
      : (choices.find((choice) => choice.templateId === pinnedId) ?? null);
  return { matches, pinned };
}

/**
 * What the foot says beside „Dalej" about the start: „Pusty plan", or the chosen template's name,
 * length and system. `chosen` is the template row for `templateId`, or `null` while it is not among
 * the rows the library answered — the read is still open — so the foot says nothing rather than
 * something untrue.
 */
export function startSummary(
  templateId: number | null,
  chosen: TemplateChoice | null,
  t: Translate,
): string | null {
  if (templateId === null) return t('web:planCreate.pick.summaryScratch');
  return chosen?.summary ?? null;
}
